import { useLocation } from '@solidjs/router';
import { createEffect, onCleanup } from 'solid-js';
import { useAuth } from '~/contexts/global';
import { sendHeartbeat } from '~/utils/presenceHeartbeat';

const HEARTBEAT_INTERVAL_MS = 60_000;

const SNOWFLAKE_ID_SEGMENT = /\/\d{10,20}(?=\/|$)/g;

export const routeTemplateFromPath = (pathname: string): string => pathname.replace(SNOWFLAKE_ID_SEGMENT, '/:id');

export const usePresenceHeartbeat = (): void => {
  const { me } = useAuth();
  const location = useLocation();

  createEffect(() => {
    // Register cleanup first, before any early returns.
    // If me() becomes falsy (e.g. user logs out), the effect re-runs and
    // this cleanup fires — clearing the interval even though we never reach
    // the setInterval call below.
    let intervalId: ReturnType<typeof setInterval> | undefined = undefined;
    onCleanup(() => clearInterval(intervalId));

    if (!me()?.id) return;

    // e.g. `/channels/123456789012345678/messages` → `/channels/:id/messages`
    const currentRoute = routeTemplateFromPath(location.pathname);

    // Send immediately on mount or whenever the route / auth state changes,
    // so the server always gets a fresh signal without waiting a full interval.
    void sendHeartbeat(currentRoute);

    // Then keep sending on a fixed cadence for as long as this effect is live.
    // onCleanup above will clear this when the effect re-runs or the component unmounts.
    intervalId = setInterval(() => void sendHeartbeat(currentRoute), HEARTBEAT_INTERVAL_MS);
  });
};
